import { useState } from "react";
import {
  BooleanInput,
  NumberInput,
  TextInput,
  required,
} from "react-admin";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import {
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineMail,
  HiOutlineServer,
} from "react-icons/hi";

// Chaves cujo VALOR é segredo: nunca exibir em texto puro por padrão
// (a listagem antiga imprimia a senha do SMTP aberta na tela)
export const SECRET_KEY_RE = /pass|senha|secret|token|key/i;

export const isSecretKey = (key: string) => SECRET_KEY_RE.test(key);

export const MASK = "••••••••";

// Configurações conhecidas (levantamento no código do servidor em
// 05/08/2026): hoje só o SMTP é consumido — app.mail.service.ts busca
// EXATAMENTE o nome "SMTP" (host, user, pass e opcionais port/secure)
// e o salvar recarrega o transporte na hora, sem reiniciar o servidor.
export const settingMeta = (
  name?: string | null
): {
  label: string;
  description: string;
  Icon: React.ElementType;
  renameWarning?: string;
} => {
  if (String(name).toUpperCase() === "SMTP") {
    return {
      label: "Servidor de e-mail (SMTP)",
      description:
        "Credenciais usadas pelo sistema para enviar os e-mails automáticos (boas-vindas, recuperação de senha e avisos de contato). Alterações valem imediatamente, sem reiniciar o servidor.",
      Icon: HiOutlineMail,
      renameWarning:
        'O servidor procura exatamente o nome "SMTP" — renomear desliga o envio de e-mails.',
    };
  }
  return {
    label: String(name || "Configuração"),
    description:
      "Configuração genérica (nome + valor em JSON). Nenhum serviço do servidor consome este nome hoje.",
    Icon: HiOutlineServer,
  };
};

// Campos amigáveis do SMTP (estilo plugin de e-mail do WordPress),
// compartilhados entre Editar e Novo. Gravam nas chaves reais do JSON
// (value.host, value.user…), preservando chaves extras no salvar.
// "secure" é liga/desliga (SSL): é o único formato que o backend lê.
export const SmtpValueInputs = () => {
  const [showPass, setShowPass] = useState(false);
  return (
    <>
      <Grid item xs={12} md={8}>
        <TextInput
          source="value.host"
          label="Servidor SMTP"
          fullWidth
          helperText={false}
          validate={required()}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <NumberInput
          source="value.port"
          label="Porta"
          fullWidth
          helperText="Vazio usa a porta padrão do provedor"
        />
      </Grid>
      <Grid item xs={12}>
        <BooleanInput
          source="value.secure"
          label="Conexão segura (SSL — porta 465)"
          helperText={false}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput
          source="value.user"
          label="Usuário (e-mail)"
          fullWidth
          helperText={false}
          validate={required()}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        {/* Senha mascarada com olhinho; autoComplete off para o
            navegador não injetar a senha do operador */}
        <TextInput
          source="value.pass"
          label="Senha"
          type={showPass ? "text" : "password"}
          autoComplete="new-password"
          fullWidth
          helperText={false}
          validate={required()}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Mostrar/ocultar senha"
                  size="small"
                  edge="end"
                  onClick={() => setShowPass((v) => !v)}
                >
                  {showPass ? (
                    <HiOutlineEyeOff size={18} />
                  ) : (
                    <HiOutlineEye size={18} />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
    </>
  );
};

// Valor plano (objeto simples) rende campos rotulados; qualquer outra
// coisa cai no visualizador de JSON bruto
export const isPlainObject = (value: any): value is Record<string, any> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const displayValue = (value: any): string =>
  value === null || value === undefined
    ? "—"
    : isPlainObject(value) || Array.isArray(value)
    ? JSON.stringify(value)
    : String(value);
